import type { Edge, Node } from "@xyflow/react";
import type {
  AdminNodeAction,
  NodeExecutionDirective,
  PipelineLane,
  PipelineNodeRuntimeStatus,
} from "@/types/aicheck";

export type PipelineNodeStatus = PipelineNodeRuntimeStatus;

export type { PipelineLane };

export interface CheckInfo {
  checkId?: string;
  name: string;
  status: PipelineNodeStatus;
  tokensReceived?: number;
  bytesReceived?: number;
  contentLength?: number;
  attempt?: number;
  error?: string;
}

interface AdminNodeActionProps {
  nodeId?: string;
  nodeExecutionDirectives?: NodeExecutionDirective[];
  onAdminAction?: (action: AdminNodeAction, nodeId: string) => void;
}

export interface SystemNodeData {
  [key: string]: unknown;
  nodeType: "system";
  label: string;
  status: PipelineNodeStatus;
  lane: PipelineLane;
  error?: string;
  tokensReceived?: number;
  bytesReceived?: number;
  contentLength?: number;
  elapsedMs?: number;
}
export interface SystemNodeData extends AdminNodeActionProps {}

export interface AICheckNodeData {
  [key: string]: unknown;
  nodeType: "ai-check";
  label: string;
  status: PipelineNodeStatus;
  lane: PipelineLane;
  checks: CheckInfo[];
  batchSize: number;
  tokensReceived?: number;
  bytesReceived?: number;
  contentLength?: number;
  elapsedMs?: number;
}
export interface AICheckNodeData extends AdminNodeActionProps {}

export interface ConsistencyRuleInfo {
  name: string;
  passed: boolean;
}

export interface ConsistencyNodeData {
  [key: string]: unknown;
  nodeType: "consistency";
  label: string;
  status: PipelineNodeStatus;
  lane: PipelineLane;
  ruleCount: number;
  passedCount: number;
  failedCount: number;
  warningCount: number;
  rules?: ConsistencyRuleInfo[];
}
export interface ConsistencyNodeData extends AdminNodeActionProps {}

export interface SummaryNodeData {
  [key: string]: unknown;
  nodeType: "summary";
  label: string;
  status: PipelineNodeStatus;
  lane: PipelineLane;
  checks: CheckInfo[];
  batchSize: number;
  tokensReceived?: number;
  bytesReceived?: number;
  contentLength?: number;
  elapsedMs?: number;
}
export interface SummaryNodeData extends AdminNodeActionProps {}

export type PipelineNodeData =
  | SystemNodeData
  | AICheckNodeData
  | ConsistencyNodeData
  | SummaryNodeData;

export type PipelineNode = Node<PipelineNodeData>;
export type PipelineEdge = Edge<{ status: PipelineNodeStatus }>;

// Node ID constants for structural nodes
export const NODE_IDS = {
  FETCH: "sys-fetch",
  CONSISTENCY_INDEPENDENT: "consistency-independent",
  COMPACTION: "sys-compaction",
  PROFILE: "sys-profile",
  CONSISTENCY_PROFILE: "consistency-profile",
} as const;
